import React, { useState, useEffect } from 'react';
import { ClockCircleOutlined, BookOutlined, UserOutlined, CalendarOutlined, FlagOutlined, DeleteOutlined, CommentOutlined, SendOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { List, Avatar, Button, Input, Modal, message, Form } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/tr';
import { PRIORITY, STATUS, API_URL, ADMIN } from '../config/Config.js';
import { calculateRemainingTime } from '../services/remainingTimeService';
import { useUser } from '../contexts/UserContext';
import './TaskCard.css';

dayjs.locale('tr');
dayjs.extend(utc);
dayjs.extend(timezone);

const { TextArea } = Input;
const { confirm } = Modal;

// Helper component for user avatar with error handling
const UserAvatar = ({ userInfo, size = 'normal' }) => {
    const [imgError, setImgError] = useState(false);

    const hasValidUrl = userInfo.url && userInfo.url.trim() !== '' && !imgError;
    const sizeClass = size === 'small' ? 'task-card-avatar-small' : size === 'xsmall' ? 'task-card-avatar-xsmall' : '';

    if (hasValidUrl) {
        return (
            <img
                src={userInfo.url}
                alt={userInfo.name}
                onError={() => setImgError(true)}
                className={sizeClass}
            />
        );
    }

    return (
        <div className={`task-card-avatar-placeholder ${sizeClass}`}>
            <UserOutlined />
        </div>
    );
};

// Get progress bar color based on percentage
const getProgressColor = (progress) => {
    if (progress >= 100) return '#22c55e'; // Green - completed
    if (progress >= 50) return '#eab308';  // Yellow - middle
    return '#ef4444'; // Red - start
};

const TaskCard = ({ task, users, onEdit, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
    const { user } = useUser();
    const [commentList, setCommentList] = useState([]);
    const [noteForm] = Form.useForm();
    const [submittingNote, setSubmittingNote] = useState(false);

    const taskData = task.task;
    const creator = task.creator;
    const categories = task.categories || [];
    const assignedUsers = task.assignedUsers || [];

    // Check if current user is the creator
    const isCreator = user.id === creator.id;
    // Check if current user is assigned to this task
    const isAssignedUser = assignedUsers.some(u => u.id === user.id);

    // Notes Logic
    useEffect(() => {
        if (task.notes && users && users.length > 0) {
            const processNotes = () => {
                const updatedNotes = task.notes.map(note => {
                    const matchedUser = users.find(u => u.id === note.userId);
                    return {
                        ...note,
                        user: matchedUser || { name: 'Unknown', surname: '', url: '' }
                    };
                });
                setCommentList(updatedNotes.sort((a, b) => new Date(b.date) - new Date(a.date)));
            };
            processNotes();
        } else if (task.notes) {
            // Fallback if users not loaded yet or empty, just show notes with userId
            const updatedNotes = task.notes.map(note => ({
                ...note,
                user: { name: 'User', surname: '', url: '' } // Placeholder
            }));
            setCommentList(updatedNotes.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
    }, [task.notes, users]);

    const handleSendNote = async () => {
        try {
            const values = await noteForm.validateFields();
            setSubmittingNote(true);

            const comment = {
                taskId: taskData.id,
                userId: user.id,
                date: dayjs().tz('Europe/Istanbul').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                content: values.comment,
            };

            const response = await axios.post(API_URL + 'Notes', comment);

            const newNote = {
                ...comment,
                id: response.data.id,
                user: user
            };

            setCommentList(prev => [newNote, ...prev]);
            noteForm.resetFields();
            message.success('Not eklendi');

            // Send Notifications
            const filteredUsers = assignedUsers.filter(aUser => aUser.id !== user.id);
            filteredUsers.forEach(aUser => {
                axios.post(API_URL + "Notifications/", {
                    title: `${user.name} ${user.surname} yeni bir not yazdı.`,
                    detail: `${user.name} ${user.surname}, "${taskData.title}" görevine not ekledi.`,
                    date: dayjs().tz('Europe/Istanbul').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                    userId: user.id,
                    assignedUserId: aUser.id
                }).catch(err => console.error(err));
            });

        } catch (error) {
            console.error(error);
        } finally {
            setSubmittingNote(false);
        }
    };

    const [modal, contextHolder] = Modal.useModal();

    const confirmDeleteNote = (note) => {
        if (!note.id) {
            message.error('Hata: Not ID bulunamadı.');
            return;
        }

        modal.confirm({
            title: 'Notu silmek istiyor musunuz?',
            icon: <ExclamationCircleFilled />,
            content: note.content,
            okText: 'Evet',
            okType: 'danger',
            cancelText: 'Hayır',
            zIndex: 10001, // Ensure it appears above the note modal
            onOk() {
                return axios.delete(API_URL + 'Notes/' + note.id)
                    .then(() => {
                        setCommentList(prev => prev.filter(n => n.id !== note.id));
                        message.success('Not silindi');
                    })
                    .catch((err) => {
                        console.error('Delete error:', err);
                        message.error('Silinemedi');
                    });
            }
        });
    };

    // Status mapping
    const getStatusClass = (status) => {
        switch (status) {
            case 4: return 'completed';
            case 3: return 'in-progress';
            case 2: return 'pending';
            case 1: return 'postponed';
            default: return 'pending';
        }
    };

    // Calculate remaining time
    const getDeadlineInfo = () => {
        if (taskData.status === 1 || taskData.progress === 100) {
            return null;
        }
        return calculateRemainingTime(taskData.estimatedCompleteDate);
    };

    const deadlineInfo = getDeadlineInfo();

    // Get deadline class
    const getDeadlineClass = () => {
        if (!deadlineInfo) return 'normal';
        if (deadlineInfo.status.includes('-')) return 'overdue';
        const days = parseInt(deadlineInfo.status);
        if (days <= 3) return 'soon';
        return 'normal';
    };

    // Toggle expand
    const handleCardClick = () => {
        setIsExpanded(!isExpanded);
    };

    // Get first category for collapsed view
    const primaryCategory = categories[0];

    // Collapsed Card View
    if (!isExpanded) {
        return (
            <div className="task-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                {/* Status Badge */}
                {contextHolder}
                <div className={`task-card-status ${getStatusClass(taskData.status)}`}>
                    {STATUS[taskData.status]}
                </div>

                {/* Header */}
                <div className="task-card-header">
                    <h3 className="task-card-title">{taskData.title}</h3>
                    {taskData.description && (
                        <p className="task-card-description">{taskData.description}</p>
                    )}
                </div>

                {/* Meta Info */}
                <div className="task-card-meta">
                    <span className="task-card-date">
                        <CalendarOutlined /> {dayjs(taskData.estimatedCompleteDate).format('DD.MM.YYYY')}
                    </span>
                    {deadlineInfo && (
                        <span className={`task-card-deadline ${getDeadlineClass()}`}>
                            <ClockCircleOutlined /> {deadlineInfo.status}
                        </span>
                    )}
                    {primaryCategory && (
                        <span className="task-card-category">
                            <BookOutlined /> {primaryCategory.name}
                        </span>
                    )}
                    <span className="task-card-creator">
                        Oluşturan: <strong>{creator.name} {creator.surname}</strong>
                    </span>
                </div>

                {/* Footer */}
                <div className="task-card-footer">
                    <div className="task-card-info">
                        <div className="task-card-priority">
                            <span className="task-card-priority-indicator"></span>
                            {PRIORITY[taskData.priority]}
                        </div>

                    </div>

                    <div className="task-card-avatars">
                        {assignedUsers.length > 0 ? (
                            assignedUsers.slice(0, 4).map((assignedUser, index) => (
                                <div className="task-card-avatar" key={assignedUser.id || index}>
                                    <UserAvatar userInfo={assignedUser} />
                                </div>
                            ))
                        ) : (
                            <div className="task-card-avatar">
                                <div className="task-card-avatar-placeholder">
                                    <UserOutlined />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="task-card-progress">
                    <div className="task-card-progress-bar">
                        <div
                            className="task-card-progress-fill"
                            style={{
                                width: `${taskData.progress}%`,
                                background: getProgressColor(taskData.progress)
                            }}
                        />
                    </div>
                    <div className="task-card-progress-text">{taskData.progress}%</div>
                </div>
            </div>
        );
    }

    // Expanded Card View (Mobile App Style)
    return (
        <div className="task-card-expanded-wrapper" style={{ cursor: 'default' }}>
            {contextHolder}
            {/* Header Section - Click to Collapse */}
            <div className="task-card-expanded-header" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                <div className="task-card-expanded-header-content">
                    <h2 className="task-card-expanded-title">{taskData.title}</h2>
                    <div className={`task-card-status ${getStatusClass(taskData.status)}`}>
                        {STATUS[taskData.status]}
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="task-card-progress expanded">
                    <div className="task-card-progress-bar">
                        <div
                            className="task-card-progress-fill"
                            style={{
                                width: `${taskData.progress}%`,
                                background: getProgressColor(taskData.progress)
                            }}
                        />
                    </div>
                    <div className="task-card-progress-text light">{taskData.progress}%</div>
                </div>
            </div>

            {/* Açıklama Section */}
            {taskData.description && (
                <div className="task-card-section">
                    <h4 className="task-card-section-title">Açıklama</h4>
                    <p className="task-card-section-content">{taskData.description}</p>
                </div>
            )}

            {/* Detaylar Section */}
            <div className="task-card-section">
                <h4 className="task-card-section-title">Detaylar</h4>
                <div className="task-card-detail-list">
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <FlagOutlined /> Öncelik:
                        </span>
                        <span className="task-card-detail-value priority">{PRIORITY[taskData.priority]}</span>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <BookOutlined /> Kategori:
                        </span>
                        <span className="task-card-detail-value">
                            {categories.length > 0 ? categories.map(c => c.name).join(', ') : '-'}
                        </span>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <UserOutlined /> Oluşturan:
                        </span>
                        <div className="task-card-detail-value creator">
                            <div className="task-card-creator-info">
                                <UserAvatar userInfo={creator} size="xsmall" />
                                <span className="task-card-creator-name">{creator.name} {creator.surname}</span>
                            </div>
                        </div>
                    </div>
                    <div className="task-card-detail-item">
                        <span className="task-card-detail-label">
                            <CalendarOutlined /> Tahmini Bitiş:
                        </span>
                        <span className="task-card-detail-value">
                            {dayjs(taskData.estimatedCompleteDate).format('DD MMMM YYYY')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Görevli Kişiler Section */}
            <div className="task-card-section">
                <h4 className="task-card-section-title">Görevli Kişiler</h4>
                <div className="task-card-users-chips">
                    {assignedUsers.map((u, idx) => (
                        <div className="task-card-user-chip" key={u.id || idx}>
                            <div className="task-card-user-chip-avatar">
                                <UserAvatar userInfo={u} size="small" />
                            </div>
                            <span>{u.name} {u.surname}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Notlar Button Section - REPLACED INLINE NOTES WITH MODAL TRIGGER */}
            <div className="task-card-section">
                <Button
                    type="default"
                    block
                    icon={<CommentOutlined />}
                    onClick={() => setIsNotesModalVisible(true)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '40px', borderColor: '#d9d9d9' }}
                >
                    Notlar ({commentList.length})
                </Button>
            </div>

            {/* Action to Edit and Delete - Only for creator */}
            {isCreator && (
                <div className="task-card-section" style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button className="task-card-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(task); }} style={{ flex: 1 }}>
                        Görevi Düzenle
                    </button>
                    {taskData.progress !== 100 && (
                        <button className="task-card-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(task); }}>
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            )}
            {/* Progress update button for assigned users who are not the creator */}
            {!isCreator && isAssignedUser && (
                <div className="task-card-section" style={{ marginTop: '10px' }}>
                    <button className="task-card-edit-btn" onClick={(e) => { e.stopPropagation(); onEdit(task); }} style={{ width: '100%' }}>
                        İlerleme Güncelle
                    </button>
                </div>
            )}

            {/* NOTES MODAL */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CommentOutlined />
                        <span>Notlar - {taskData.title}</span>
                    </div>
                }
                open={isNotesModalVisible}
                onCancel={() => setIsNotesModalVisible(false)}
                footer={null}
                centered
                destroyOnClose
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '4px' }}
            >
                {/* Not Ekleme Formu */}
                <div style={{ marginBottom: '16px', paddingTop: '10px' }}>
                    <Form form={noteForm} onFinish={handleSendNote}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Form.Item name="comment" style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Not yazınız' }]}>
                                <Input.TextArea
                                    placeholder="Not ekle..."
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                    disabled={taskData.progress === 100}
                                />
                            </Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SendOutlined />}
                                loading={submittingNote}
                                disabled={taskData.progress === 100}
                                style={{ backgroundColor: '#3F72AF', height: 'auto' }}
                            />
                        </div>
                    </Form>
                </div>

                {/* Not Listesi */}
                <div className="task-card-comments-list">
                    {commentList.length > 0 ? (
                        <List
                            itemLayout="horizontal"
                            dataSource={commentList}
                            split={false}
                            renderItem={comment => (
                                <List.Item
                                    style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
                                    actions={
                                        ((taskData.progress !== 100) && ((user.id === comment.user.id) || (user.role === ADMIN)))
                                            ? [<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDeleteNote(comment)} />]
                                            : []
                                    }
                                >
                                    <List.Item.Meta
                                        avatar={<UserAvatar userInfo={comment.user} size="xsmall" />}
                                        title={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', flexWrap: 'wrap', gap: '4px' }}>
                                                <span style={{ fontWeight: 600 }}>{comment.user.name} {comment.user.surname}</span>
                                                <span style={{ color: '#999', fontSize: '10px' }}>{dayjs(comment.date).format('DD.MM HH:mm')}</span>
                                            </div>
                                        }
                                        description={<div style={{ color: '#333', fontSize: '13px', lineHeight: '1.4', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{comment.content}</div>}
                                    />
                                </List.Item>
                            )}
                        />
                    ) : (
                        <div style={{ textAlign: 'center', color: '#999', padding: '20px', fontSize: '14px', background: '#fafafa', borderRadius: '8px' }}>Henüz not eklenmemiş.</div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default TaskCard;
